// src/pages/ScoreboardPublic/ScoreboardPublic.jsx  (ou ton chemin actuel)
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import socket from "./../../components/ScoreboardAdmin/socketClient";
import { getCompetitionById } from "./../../services/competitionsServices";

/* =========================
   OPTIONS Catégories (France)
   ========================= */
const AGE_OPTIONS_FR = [
  { label: "Éveil", key: "Éveil" },
  { label: "Mini-Poussin(e)", key: "Mini-Poussin(e)" },
  { label: "Poussin(e)", key: "Poussin(e)" },
  { label: "Benjamin(e)", key: "Benjamin(e)" },
  { label: "Minime", key: "Minime" },
  { label: "Cadet(te)", key: "Cadet(te)" },
  { label: "Junior", key: "Junior" },
  { label: "Sénior", key: "Sénior" },
  { label: "Vétéran", key: "Vétéran" },

  { label: "Benjamins", key: "Benjamin(e)" },
  { label: "Benjamines", key: "Benjamin(e)" },
  { label: "Minimes", key: "Minime" },
  { label: "Cadets", key: "Cadet(te)" },
  { label: "Cadettes", key: "Cadet(te)" },
  { label: "Juniors", key: "Junior" },
  { label: "Seniors", key: "Sénior" },
  { label: "Vétérans", key: "Vétéran" },
];

/* =========================
   Catégories de poids (France)
   ========================= */
const WEIGHT_CATEGORIES_FR = {
  "Benjamin(e)": [26, 30, 34, 38, 42, 46, 50, 55, 60, 66],
  Minime: [34, 38, 42, 46, 50, 55, 60, 66, 73],
  "Cadet(te)": [46, 50, 55, 60, 66, 73, 81, 90],
  Junior: [55, 60, 66, 73, 81, 90, 100],
  Sénior: [60, 66, 73, 81, 90, 100],
  Vétéran: [60, 66, 73, 81, 90, 100],

  "Benjamin(e)F": [28, 32, 36, 40, 44, 48, 52, 57, 63],
  MinimeF: [36, 40, 44, 48, 52, 57, 63, 70],
  "Cadet(te)F": [40, 44, 48, 52, 57, 63, 70],
  JuniorF: [44, 48, 52, 57, 63, 70, 78],
  SéniorF: [48, 52, 57, 63, 70, 78],
  VétéranF: [48, 52, 57, 63, 70, 78],
};

const isGMCategory = (ageKey) =>
  ["Éveil", "Mini-Poussin(e)", "Poussin(e)"].includes(ageKey);

const ageKeyFromLabel = (label) => {
  const found = AGE_OPTIONS_FR.find((o) => o.label === label);
  return found?.key || label || "";
};

const buildWeightOptionsFR = (ageLabel, gender /* "M"|"F" */) => {
  const ageKey = ageKeyFromLabel(ageLabel);
  if (!ageKey) return [];

  if (isGMCategory(ageKey)) return ["GM"];

  const key = gender === "F" ? `${ageKey}F` : ageKey;
  const arr = WEIGHT_CATEGORIES_FR[key];
  if (!arr || !arr.length) return [];

  const last = arr[arr.length - 1];
  const minus = arr.map((n) => `-${n}kg`);
  return [...minus, `+${last}kg`];
};

/* =========================
   Modale Mémo
   ========================= */
function MemoModal({ open, seedId, initialValue, onClose, onSave }) {
  const [draft, setDraft] = useState(initialValue);

  useEffect(() => {
    if (open) setDraft(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seedId]);

  const weightOptions = useMemo(() => {
    return buildWeightOptionsFR(draft.ageCategory, draft.gender);
  }, [draft.ageCategory, draft.gender]);

  useEffect(() => {
    if (!open) return;
    if (!draft.ageCategory) {
      if (draft.weightCategory) setDraft((d) => ({ ...d, weightCategory: "" }));
      return;
    }

    const ageKey = ageKeyFromLabel(draft.ageCategory);
    if (isGMCategory(ageKey)) {
      if (draft.weightCategory !== "GM") {
        setDraft((d) => ({ ...d, weightCategory: "GM" }));
      }
      return;
    }

    if (draft.weightCategory && !weightOptions.includes(draft.weightCategory)) {
      setDraft((d) => ({ ...d, weightCategory: "" }));
    }
  }, [
    open,
    draft.ageCategory,
    draft.gender,
    draft.weightCategory,
    weightOptions,
  ]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
        e.preventDefault();
        onSave({
          gender: draft.gender || "",
          ageCategory: draft.ageCategory || "",
          weightCategory: draft.weightCategory || "",
        });
      }
    };
    if (open) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, draft, onClose, onSave]);

  if (!open) return null;

  const chip =
    "inline-flex items-center justify-center rounded-full border px-6 py-3 font-black " +
    "text-[clamp(1.4rem,3.6vmin,3.2rem)] bg-white/10 border-white/20 text-white";

  return (
    <div className="fixed inset-0 z-[80]">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          className="w-full max-w-3xl rounded-2xl border border-white/15 bg-slate-950/95 text-white shadow-2xl backdrop-blur overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 sm:px-8 py-6 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="font-black text-[clamp(1.8rem,4vmin,3.4rem)]">
                Saisie mémo (table d’arbitrage)
              </div>
              <div className="text-white/60 mt-2 text-[clamp(1rem,2vmin,1.25rem)]">
                Sert à afficher Âge / Genre / Poids en attente/pause (et
                fallback si non transmis).
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-5 py-3 bg-white/10 hover:bg-white/15 border border-white/15 font-extrabold"
            >
              Fermer
            </button>
          </div>

          <div className="px-6 sm:px-8 py-7">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="block font-extrabold text-white/85 mb-2 text-[clamp(1.1rem,2.4vmin,1.6rem)]">
                  Genre
                </label>
                <select
                  value={draft.gender}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, gender: e.target.value }))
                  }
                  className="w-full rounded-xl bg-black/40 border border-white/15 text-white px-4 py-4 font-black outline-none
                             text-[clamp(1.2rem,2.8vmin,2rem)]"
                  autoFocus
                >
                  <option value="">—</option>
                  <option value="M">Masculin</option>
                  <option value="F">Féminin</option>
                </select>
              </div>

              <div>
                <label className="block font-extrabold text-white/85 mb-2 text-[clamp(1.1rem,2.4vmin,1.6rem)]">
                  Catégorie d’âge (France)
                </label>
                <select
                  value={draft.ageCategory}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, ageCategory: e.target.value }))
                  }
                  className="w-full rounded-xl bg-black/40 border border-white/15 text-white px-4 py-4 font-black outline-none
                             text-[clamp(1.2rem,2.8vmin,2rem)]"
                >
                  <option value="">—</option>
                  {AGE_OPTIONS_FR.map((o) => (
                    <option key={`${o.label}-${o.key}`} value={o.label}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-extrabold text-white/85 mb-2 text-[clamp(1.1rem,2.4vmin,1.6rem)]">
                  Catégorie de poids
                </label>
                <select
                  value={draft.weightCategory}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, weightCategory: e.target.value }))
                  }
                  className="w-full rounded-xl bg-black/40 border border-white/15 text-white px-4 py-4 font-black outline-none
                             text-[clamp(1.2rem,2.8vmin,2rem)]"
                  disabled={!draft.ageCategory}
                >
                  <option value="">—</option>
                  {weightOptions.map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>

                {!draft.ageCategory && (
                  <div className="mt-2 text-white/45 text-[clamp(.95rem,1.8vmin,1.1rem)]">
                    Choisis d’abord la catégorie d’âge.
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              {draft.ageCategory && (
                <span className={chip}>{draft.ageCategory}</span>
              )}
              {draft.gender && (
                <span
                  className={`${chip} ${
                    draft.gender === "F"
                      ? "bg-pink-600/85 border-pink-400/40"
                      : draft.gender === "M"
                        ? "bg-blue-600/85 border-blue-400/40"
                        : ""
                  }`}
                >
                  {draft.gender === "F" ? "Féminin" : "Masculin"}
                </span>
              )}
              {draft.weightCategory && (
                <span className={chip}>{draft.weightCategory}</span>
              )}
              {!draft.ageCategory && !draft.gender && !draft.weightCategory && (
                <span className="text-white/45 text-[clamp(1.1rem,2.4vmin,1.6rem)]">
                  Rien saisi pour le moment.
                </span>
              )}
            </div>

            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <button
                type="button"
                onClick={() =>
                  setDraft({ gender: "", ageCategory: "", weightCategory: "" })
                }
                className="rounded-xl px-6 py-4 bg-white/10 hover:bg-white/15 border border-white/15 font-extrabold
                           text-[clamp(1.1rem,2.4vmin,1.6rem)]"
              >
                Réinitialiser
              </button>
              <button
                type="button"
                onClick={() =>
                  onSave({
                    gender: draft.gender || "",
                    ageCategory: draft.ageCategory || "",
                    weightCategory: draft.weightCategory || "",
                  })
                }
                className="rounded-xl px-7 py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-black
                           text-[clamp(1.1rem,2.4vmin,1.6rem)]"
              >
                Enregistrer
              </button>
            </div>

            <div className="mt-4 text-center text-white/40 text-[clamp(.9rem,1.8vmin,1.1rem)]">
              Astuce : <b>Échap</b> pour fermer • <b>Ctrl/⌘ + Entrée</b> pour
              enregistrer
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Next fight helpers / UI
   ========================= */
const fullName = (p) =>
  `${String(p?.nom || "").toUpperCase()} ${p?.prenom || ""}`.trim();

function NextFightTopBar({ nextFight, nextMsg, tapisId }) {
  if (!nextFight) return null;

  const genderLabel =
    nextFight?.genre === "F"
      ? "Féminin"
      : nextFight?.genre === "M"
        ? "Masculin"
        : nextFight?.genre || "";

  return (
    <div className="w-full flex items-center justify-between gap-5">
      {/* ROUGE */}
      <div className="min-w-0 flex-1">
        <div className="inline-flex items-center gap-3 rounded-2xl bg-red-600 text-white px-5 py-3 shadow-xl">
          {/* <div className="text-[clamp(1.2rem,2.4vmin,2.2rem)] font-black tracking-widest">
            ROUGE
          </div> */}
          <div className="min-w-0">
            <div className="truncate text-[clamp(1.8rem,3.6vmin,3.2rem)] font-black leading-none">
              {fullName(nextFight.c1)}
            </div>
            {!!nextFight?.c1?.club && (
              <div className="truncate text-white/90 text-[clamp(1.1rem,2.2vmin,2rem)] font-extrabold">
                {nextFight.c1.club}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CENTRE */}
      <div className="flex flex-col items-center justify-center text-center px-2">
        <div className="text-yellow-300 font-black tracking-[.25em] text-[clamp(1.1rem,2.2vmin,2rem)]">
          PROCHAIN COMBAT • TAPIS {tapisId}
        </div>
        <div className="mt-1 text-white/90 font-extrabold text-[clamp(1.2rem,2.4vmin,2.2rem)]">
          #{nextFight.distribution_id} • {genderLabel} •{" "}
          {nextFight.categorie_age} • {nextFight.categorie_poids}
        </div>
        {!!nextMsg && (
          <div className="mt-1 text-white text-[clamp(1.2rem,2.4vmin,2.2rem)] font-black">
            {nextMsg}
          </div>
        )}
      </div>

      {/* BLANC */}
      <div className="min-w-0 flex-1 flex justify-end">
        <div className="inline-flex items-center gap-3 rounded-2xl bg-white text-black px-5 py-3 shadow-xl">
          {/* <div className="text-[clamp(1.2rem,2.4vmin,2.2rem)] font-black tracking-widest">
            BLANC
          </div> */}
          <div className="min-w-0">
            <div className="truncate text-[clamp(1.8rem,3.6vmin,3.2rem)] font-black leading-none">
              {fullName(nextFight.c2)}
            </div>
            {!!nextFight?.c2?.club && (
              <div className="truncate text-black/70 text-[clamp(1.1rem,2.2vmin,2rem)] font-extrabold">
                {nextFight.c2.club}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function NextFightBanner({ nextFight, nextMsg }) {
  if (!nextFight && !nextMsg) return null;

  const genderLabel =
    nextFight?.genre === "F"
      ? "Féminin"
      : nextFight?.genre === "M"
        ? "Masculin"
        : nextFight?.genre || "";

  return (
    <div className="mt-6 rounded-2xl border border-white/15 bg-white/10 p-6 shadow-2xl backdrop-blur">
      <div className="text-white/70 font-black tracking-[.25em] text-[clamp(1.2rem,2.6vmin,2.2rem)]">
        PROCHAIN COMBAT
      </div>

      {nextFight ? (
        <>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-4">
            <span className="rounded-2xl bg-red-600 text-white px-6 py-4 font-black text-[clamp(2rem,4.4vmin,4.8rem)] leading-none shadow-xl">
              {fullName(nextFight.c1)}
            </span>
            <span className="text-white/60 font-black text-[clamp(1.6rem,3.2vmin,3.4rem)]">
              vs
            </span>
            <span className="rounded-2xl bg-white text-black px-6 py-4 font-black text-[clamp(2rem,4.4vmin,4.8rem)] leading-none shadow-xl">
              {fullName(nextFight.c2)}
            </span>
          </div>

          <div className="mt-4 text-white/85 font-extrabold text-[clamp(1.3rem,2.8vmin,2.4rem)] text-center">
            #{nextFight.distribution_id} • {genderLabel} •{" "}
            {nextFight.categorie_age} • {nextFight.categorie_poids}
          </div>
        </>
      ) : (
        <div className="mt-2 text-[clamp(2rem,4.5vmin,3.4rem)] italic text-white/80">
          —
        </div>
      )}

      {!!nextMsg && (
        <div className="mt-3 text-center text-[clamp(1.6rem,3.2vmin,2.8rem)] text-white font-black">
          {nextMsg}
        </div>
      )}
    </div>
  );
}

/* =========================
   ScoreboardPublic
   ========================= */
const ScoreboardPublic = () => {
  const { competitionId, tapisId } = useParams();
  const [combatData, setCombatData] = useState(null);
  const [competitionName, setCompetitionName] = useState("");
  const [isPaused, setIsPaused] = useState(false);

  // ✅ prochain combat reçu via socket
  const [nextFight, setNextFight] = useState(null);
  const [nextMsg, setNextMsg] = useState("");

  // ===== Memo (stocké localStorage par tapis) =====
  const memoKey = useMemo(
    () => `scoreboard_public_memo_${competitionId}_${tapisId}`,
    [competitionId, tapisId],
  );

  const [memo, setMemo] = useState({
    gender: "",
    ageCategory: "",
    weightCategory: "",
  });

  const [memoModalOpen, setMemoModalOpen] = useState(false);
  const [memoSeedId, setMemoSeedId] = useState(0);
  const [memoSeed, setMemoSeed] = useState(memo);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(memoKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        setMemo((prev) => ({ ...prev, ...parsed }));
      }
    } catch (e) {
      console.warn("Memo localStorage invalid:", e);
    }
  }, [memoKey]);

  useEffect(() => {
    try {
      localStorage.setItem(memoKey, JSON.stringify(memo));
    } catch {
      // ignore
    }
  }, [memoKey, memo]);

  const openMemoModal = () => {
    setMemoSeed(memo);
    setMemoSeedId((n) => n + 1);
    setMemoModalOpen(true);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  // Charger nom compétition
  useEffect(() => {
    const fetchCompetition = async () => {
      try {
        const data = await getCompetitionById(competitionId);
        setCompetitionName(data.nom_competition);
      } catch (err) {
        console.error("Erreur chargement compétition :", err);
      }
    };
    fetchCompetition();
  }, [competitionId]);

  // Socket
  useEffect(() => {
    const roomKey = `${competitionId}_${tapisId}`;
    socket.emit("join_combat", roomKey);

    const onScoreUpdate = ({ data }) => {
      if (!data) return;

      // Ignore les payloads JJ Combat dans l'écran Judo
      if (data.redScore || data.blueScore) {
        return;
      }

      setCombatData(data);
    };

    const onPause = ({ paused }) => setIsPaused(!!paused);

    const onNextFight = ({ nextFight, message }) => {
      setNextFight(nextFight || null);
      setNextMsg((message || "").toString());
    };

    socket.on("score_update", onScoreUpdate);
    socket.on("public_pause", onPause);
    socket.on("public_next_fight", onNextFight);

    return () => {
      socket.off("score_update", onScoreUpdate);
      socket.off("public_pause", onPause);
      socket.off("public_next_fight", onNextFight);
    };
  }, [competitionId, tapisId]);

  const FS = {
    title: "text-[clamp(2.4rem,5.2vmin,6.2rem)]",
    tapisHuge: "text-[clamp(10rem,30vmin,38vh)]",
    subtitle: "text-[clamp(2rem,4.2vmin,5rem)]",
    chip: "text-[clamp(1.8rem,4.2vmin,4.2rem)]",

    topCompetition: "text-[clamp(2.2rem,4.4vmin,5.8rem)]",
    topTapis: "text-[clamp(3.6rem,7vmin,8.5rem)]",

    nameLast: "text-[clamp(3rem,6.4vmin,7rem)]",
    nameFirst: "text-[clamp(2rem,4.4vmin,4.6rem)]",
    club: "text-[clamp(1.6rem,3.4vmin,3.8rem)]",

    score: "text-[clamp(6rem,22vmin,26vh)]",
    scoreMinW: "min-w-[clamp(5.8rem,14.5vmin,11.5rem)]",

    osaekomi: "text-[clamp(2rem,10vmin,10vh)]",
    penaltyBox:
      "w-[clamp(3.8rem,6.2vmin,5.6rem)] h-[clamp(4.4rem,7.4vmin,6.8rem)]",
    penaltyTxt: "text-[clamp(2.2rem,4vmin,3.6rem)]",

    categorie: "text-[clamp(2.8rem,5.6vmin,6.6rem)]",
    categorieSub: "text-[clamp(2.2rem,4.6vmin,5.4rem)]",
    chrono: "text-[clamp(7.2rem,22vmin,24vh)]",
  };

  const combatWeightLabel = useMemo(() => {
    if (!combatData) return "";
    return (
      combatData.categoriePoids ||
      combatData.categorie_poids ||
      combatData.weightCategory ||
      combatData.categoriePoidsLabel ||
      combatData.categorie_poids_label ||
      ""
    );
  }, [combatData]);

  const weightLabelToShow = String(
    combatWeightLabel || memo.weightCategory || "",
  ).trim();

  const Chips = () => {
    const gLabel =
      memo.gender === "F"
        ? "Féminin"
        : memo.gender === "M"
          ? "Masculin"
          : memo.gender;

    const hasAny = !!memo.ageCategory || !!memo.gender || !!memo.weightCategory;
    if (!hasAny) return null;

    const baseChip =
      "inline-flex items-center justify-center rounded-full px-8 py-4 " +
      `${FS.chip} font-black border border-white/15 bg-white/10 text-white shadow-xl`;

    return (
      <div className="mt-10 flex flex-wrap items-center justify-center gap-5">
        {memo.ageCategory && (
          <span className={baseChip}>{memo.ageCategory}</span>
        )}
        {memo.gender && (
          <span
            className={`${baseChip} ${
              memo.gender === "F"
                ? "bg-pink-600/85 border-pink-400/40"
                : memo.gender === "M"
                  ? "bg-blue-600/85 border-blue-400/40"
                  : ""
            }`}
          >
            {gLabel}
          </span>
        )}
        {memo.weightCategory && (
          <span className={baseChip}>{memo.weightCategory}</span>
        )}
      </div>
    );
  };

  const FloatingMemoButton = () => (
    <button
      type="button"
      onClick={openMemoModal}
      className="fixed bottom-8 right-8 z-[60] rounded-full w-16 h-16 sm:w-20 sm:h-20
                 bg-white/10 hover:bg-white/15 border border-white/15
                 text-white font-black shadow-2xl backdrop-blur
                 flex items-center justify-center
                 opacity-45 hover:opacity-100 transition"
      title="Saisie mémo"
      aria-label="Saisie mémo"
    >
      <span className="text-3xl sm:text-4xl leading-none">✎</span>
    </button>
  );

  // =========================
  // Écrans PAUSE / ATTENTE
  // =========================
  if (isPaused) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-black text-white text-center p-6">
        <h1 className={`${FS.title} font-black mb-8`}>{competitionName}</h1>

        <h2
          className={`${FS.tapisHuge} font-black text-yellow-400 leading-none`}
        >
          TAPIS {tapisId}
        </h2>

        <p className={`${FS.subtitle} mt-10 text-gray-300 font-semibold`}>
          Tapis en pause
        </p>

        <Chips />
        <NextFightBanner nextFight={nextFight} nextMsg={nextMsg} />

        <FloatingMemoButton />
        <MemoModal
          open={memoModalOpen}
          seedId={memoSeedId}
          initialValue={memoSeed}
          onClose={() => setMemoModalOpen(false)}
          onSave={(v) => {
            setMemo(v);
            setMemoModalOpen(false);
          }}
        />
      </div>
    );
  }

  if (!combatData) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-black text-white text-center p-6">
        <h1 className={`${FS.title} font-black mb-8`}>{competitionName}</h1>

        <h2
          className={`${FS.tapisHuge} font-black text-green-400 leading-none`}
        >
          TAPIS {tapisId}
        </h2>

        <p className={`${FS.subtitle} mt-10 text-gray-400 font-semibold`}>
          En attente du combat...
        </p>

        <Chips />
        <NextFightBanner nextFight={nextFight} nextMsg={nextMsg} />

        <FloatingMemoButton />
        <MemoModal
          open={memoModalOpen}
          seedId={memoSeedId}
          initialValue={memoSeed}
          onClose={() => setMemoModalOpen(false)}
          onSave={(v) => {
            setMemo(v);
            setMemoModalOpen(false);
          }}
        />
      </div>
    );
  }

  // =========================
  // Écran COMBAT
  // =========================
  const {
    competiteur1,
    competiteur2,
    score1,
    score2,
    penalites1,
    penalites2,
    osaekomi1,
    osaekomi2,
    chrono,
    goldenScoreMode,
    goldenChrono,
    categorieAge,
    chronoMax,
  } = combatData;

  const hasNextFight = !!nextFight; // ✅ si true => remplace le header tournoi+tapis

  return (
    <div className="w-screen h-screen flex flex-col text-white font-black">
      {/* Bandeau haut */}
      <div className="flex justify-between items-center bg-black text-white h-[10%] px-6">
        {hasNextFight ? (
          <NextFightTopBar
            nextFight={nextFight}
            nextMsg={nextMsg}
            tapisId={tapisId}
          />
        ) : (
          <>
            <div className={`${FS.topCompetition}`}>{competitionName}</div>
            <div className={`${FS.topTapis}`}>Tapis {tapisId}</div>
          </>
        )}
      </div>

      {/* Rouge */}
      <div className="flex flex-row items-center justify-between w-full h-[30%] bg-red-700 text-white px-8">
        <div className="flex flex-col justify-center w-[28%] min-w-[340px] max-w-[520px]">
          <div className={`${FS.nameLast} uppercase leading-none mb-2`}>
            {competiteur1.nom_competiteur}
          </div>
          <div
            className={`${FS.nameFirst} uppercase leading-none mb-4 font-semibold`}
          >
            {competiteur1.prenom_competiteur}
          </div>
          <div
            className={`${FS.club} mt-3 uppercase leading-none font-semibold`}
          >
            {competiteur1.club_competiteur}
          </div>
        </div>

        <div className="flex items-center justify-end h-full">
          <div className="flex space-x-[clamp(1.5rem,4.2vmin,4rem)] items-center h-[80%]">
            {[score1.ippon, score1.wazari, score1.yuko].map((val, i) => (
              <span
                key={i}
                className={`${FS.score} ${FS.scoreMinW} leading-none text-center tabular-nums`}
              >
                {val}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center space-y-[clamp(.6rem,1.6vmin,1rem)]">
          {[...Array(Math.min(penalites1, 2))].map((_, i) => (
            <div
              key={`shido-${i}`}
              className={`${FS.penaltyBox} bg-yellow-300 text-black ${FS.penaltyTxt} flex items-center justify-center rounded-xl shadow-2xl`}
            >
              S
            </div>
          ))}
          {score1.hansokuMake && (
            <div
              className={`${FS.penaltyBox} bg-red-600 text-white ${FS.penaltyTxt} flex items-center justify-center rounded-xl shadow-2xl`}
            >
              H
            </div>
          )}
        </div>

        <div className="flex flex-col items-end justify-center space-y-3 w-[15%] pr-4 overflow-hidden">
          <div
            className={`${FS.osaekomi} leading-none tabular-nums text-right w-full mr-8`}
          >
            {osaekomi1}s
          </div>
          <div className="w-full h-[clamp(.7rem,1.6vmin,1.25rem)] bg-gray-300 rounded overflow-hidden">
            <div
              className="h-full bg-yellow-400 transition-all duration-200"
              style={{ width: `${(osaekomi1 / 20) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Blanc */}
      <div className="flex flex-row items-center justify-between w-full h-[30%] bg-white text-black px-8">
        <div className="flex flex-col justify-center w-[28%] min-w-[340px] max-w-[520px]">
          <div className={`${FS.nameLast} uppercase leading-none mb-2`}>
            {competiteur2.nom_competiteur}
          </div>
          <div
            className={`${FS.nameFirst} uppercase leading-none mb-4 font-semibold`}
          >
            {competiteur2.prenom_competiteur}
          </div>
          <div
            className={`${FS.club} mt-3 uppercase leading-none font-semibold`}
          >
            {competiteur2.club_competiteur}
          </div>
        </div>

        <div className="flex items-center justify-end h-full">
          <div className="flex space-x-[clamp(1.5rem,4.2vmin,4rem)] items-center h-[80%]">
            {[score2.ippon, score2.wazari, score2.yuko].map((val, i) => (
              <span
                key={i}
                className={`${FS.score} ${FS.scoreMinW} leading-none text-center tabular-nums`}
              >
                {val}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center space-y-[clamp(.6rem,1.6vmin,1rem)]">
          {[...Array(Math.min(penalites2, 2))].map((_, i) => (
            <div
              key={`shido-${i}`}
              className={`${FS.penaltyBox} bg-yellow-300 text-black ${FS.penaltyTxt} flex items-center justify-center rounded-xl shadow-2xl`}
            >
              S
            </div>
          ))}
          {score2.hansokuMake && (
            <div
              className={`${FS.penaltyBox} bg-red-600 text-white ${FS.penaltyTxt} flex items-center justify-center rounded-xl shadow-2xl`}
            >
              H
            </div>
          )}
        </div>

        <div className="flex flex-col items-end justify-center space-y-3 w-[15%] pr-4 overflow-hidden">
          <div
            className={`${FS.osaekomi} leading-none tabular-nums text-right w-full`}
          >
            {osaekomi2}s
          </div>
          <div className="w-full h-[clamp(.7rem,1.6vmin,1.25rem)] bg-gray-300 rounded overflow-hidden">
            <div
              className="h-full bg-yellow-400 transition-all duration-200"
              style={{ width: `${(osaekomi2 / 20) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Bas : Catégories + Chrono */}
      <div className="flex justify-between items-center bg-black h-[30%] px-8 w-full text-white">
        <div className="flex flex-col gap-3">
          <div className={`${FS.categorie} uppercase tracking-wide`}>
            {categorieAge}
          </div>
          {!!weightLabelToShow && (
            <div className={`${FS.categorieSub} uppercase text-white/90`}>
              {weightLabelToShow}
            </div>
          )}
        </div>

        <div className="flex flex-col items-end justify-center w-full max-w-[75%]">
          <div
            className={`${FS.chrono} leading-none font-sans tabular-nums ${
              goldenScoreMode ? "text-yellow-400" : "text-white"
            }`}
          >
            {goldenScoreMode
              ? `GS ${formatTime(goldenChrono)}`
              : formatTime(chrono)}
          </div>

          {!goldenScoreMode && chronoMax && (
            <div className="mt-[clamp(1.4rem,3.2vmin,2.2rem)] w-full h-[clamp(.8rem,1.8vmin,1.5rem)] bg-gray-600 rounded overflow-hidden">
              <div
                className="h-full bg-green-400 transition-all duration-300"
                style={{
                  width:
                    chronoMax && chrono >= 0
                      ? `${Math.min(100, (chrono / chronoMax) * 100)}%`
                      : "0%",
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScoreboardPublic;
